import { useMemo, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type {
  DeclineReasonCode,
  OrderPaymentProofUrlItem,
  OrderStatus,
  PaymentMethod,
  SellerOrderDetail,
} from "@/lib/api/types";
import { formatCurrency } from "@/lib/formatting/currency";
import { getPaymentMethodLabel } from "@/modules/stores/services/payment-method-label";
import { useToast, Button, Combobox, ConfirmDialog, NumberInput } from "@a2z/ui";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import {
  ORDER_EDITABLE_STATUS_VALUES,
  getOrderStatusLabel,
  isEtaRelevantStatus,
} from "@/modules/stores/services/order-status";
import { PaymentReviewDialog } from "./PaymentReviewDialog";
import { OnlinePaymentPanel } from "./OnlinePaymentPanel";
import { OrderAddressBlock } from "./OrderAddressBlock";

type Props = {
  locale: string;
  storeId: string;
  order: SellerOrderDetail;
  proofState: {
    items: OrderPaymentProofUrlItem[];
    isLoading: boolean;
    isError: boolean;
  };
  onUpdateStatus: (payload: {
    status: OrderStatus;
    statusEtaDays: number | null;
    declineReasonCode?: DeclineReasonCode;
    declineReasonNote?: string;
  }) => Promise<SellerOrderDetail>;
};

const declineOptions: Array<{ value: DeclineReasonCode; labelKey: string }> = [
  {
    value: "amount_not_received",
    labelKey: keys.orders.declineAmountNotReceived,
  },
  { value: "amount_not_match", labelKey: keys.orders.declineAmountNotMatch },
];

function normalizeEtaValue(value: number | null): string {
  return value === null ? "" : String(value);
}

function mapStatusUpdateError(
  error: ApiJsonClientError,
  nextStatus: OrderStatus,
  t: ReturnType<typeof useTranslations>,
): string {
  // Stale conflict — the backend now emits code 'STALE_STATUS'; fall back to
  // the 409 status for responses that carry no machine code.
  if (error.code === "STALE_STATUS" || error.status === 409) {
    return t(keys.orders.paymentReviewConflictNotice);
  }

  // Validation errors arrive as class-validator 400s: a message (array) in the
  // payload with no machine code. Surface the server's message when present so
  // the reviewer sees the actionable reason; otherwise fall back to ETA copy.
  // Client-side pre-validation already blocks missing/negative/NaN ETA.
  if (error.status === 400) {
    const serverMessage = error.message.trim();
    if (serverMessage.length > 0 && serverMessage !== "Request failed") {
      return serverMessage;
    }
    if (nextStatus === "shipped" || nextStatus === "preparing") {
      return t(keys.orders.etaRequiredForStatusUpdate);
    }
    return t(keys.orders.etaMustBeValidNumber);
  }

  return t(keys.errors.generic);
}

export function OrderDetails({
  locale,
  storeId,
  order,
  proofState,
  onUpdateStatus,
}: Props) {
  const t = useTranslations();
  const toast = useToast();
  const [orderState, setOrderState] = useState(order);

  const [draftStatus, setDraftStatus] = useState<OrderStatus>(
    orderState.status,
  );
  const [draftEta, setDraftEta] = useState<string>(
    normalizeEtaValue(orderState.statusEtaDays),
  );
  const [draftDeclineCode, setDraftDeclineCode] = useState<
    DeclineReasonCode | ""
  >(orderState.declineReasonCode ?? "");
  const [draftDeclineNote, setDraftDeclineNote] = useState(
    orderState.declineReasonNote ?? "",
  );

  const [savedStatus, setSavedStatus] = useState<OrderStatus>(
    orderState.status,
  );
  const [savedEta, setSavedEta] = useState<number | null>(
    orderState.statusEtaDays,
  );
  const [savedDeclineCode, setSavedDeclineCode] =
    useState<DeclineReasonCode | null>(orderState.declineReasonCode);
  const [savedDeclineNote, setSavedDeclineNote] = useState(
    orderState.declineReasonNote ?? "",
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const proofItems = proofState.items;
  const isProofLoading = proofState.isLoading;
  const proofError = proofState.isError;

  // Re-sync local draft/saved state whenever a fresh order comes in from the
  // server (adjust-state-during-render pattern instead of an effect).
  const [prevOrder, setPrevOrder] = useState(order);
  if (order !== prevOrder) {
    setPrevOrder(order);
    setOrderState(order);
    setDraftStatus(order.status);
    setDraftEta(normalizeEtaValue(order.statusEtaDays));
    setDraftDeclineCode(order.declineReasonCode ?? "");
    setDraftDeclineNote(order.declineReasonNote ?? "");
    setSavedStatus(order.status);
    setSavedEta(order.statusEtaDays);
    setSavedDeclineCode(order.declineReasonCode);
    setSavedDeclineNote(order.declineReasonNote ?? "");
  }

  const statusOptions = useMemo(
    () =>
      ORDER_EDITABLE_STATUS_VALUES.map((status) => ({
        value: status,
        label: getOrderStatusLabel(t, status),
      })),
    [t],
  );

  const hasChanges =
    draftStatus !== savedStatus ||
    draftEta !== normalizeEtaValue(savedEta) ||
    draftDeclineCode !== (savedDeclineCode ?? "") ||
    draftDeclineNote.trim() !== savedDeclineNote.trim();
  const canReviewPayment =
    orderState.status === "pending_payment_verification" &&
    storeId.length > 0;

  const submitStatusUpdate = async (allowDecline: boolean) => {
    const etaInput = draftEta;
    const hasEtaInput = etaInput.trim() !== "";
    const etaDays = hasEtaInput ? Number(etaInput) : null;

    if (
      isEtaRelevantStatus(draftStatus) &&
      draftStatus !== "confirmed" &&
      !hasEtaInput
    ) {
      setSaveError(t(keys.orders.etaRequiredForStatusUpdate));
      return;
    }

    if (etaDays !== null && (!Number.isFinite(etaDays) || etaDays < 0)) {
      setSaveError(t(keys.orders.etaMustBeValidNumber));
      return;
    }

    if (draftStatus === "declined" && !allowDecline) {
      setShowDeclineConfirm(true);
      return;
    }

    if (draftStatus === "declined" && !draftDeclineCode) {
      setSaveError(t(keys.orders.declineReasonPlaceholder));
      return;
    }

    setSaveError(null);
    setSaveSuccess(null);
    setIsSaving(true);

    const payload: {
      status: OrderStatus;
      statusEtaDays: number | null;
      declineReasonCode?: DeclineReasonCode;
      declineReasonNote?: string;
    } = {
      status: draftStatus,
      statusEtaDays: isEtaRelevantStatus(draftStatus) ? etaDays : null,
    };

    if (draftStatus === "declined") {
      payload.declineReasonCode = draftDeclineCode as DeclineReasonCode;
      payload.declineReasonNote = draftDeclineNote || undefined;
    }

    try {
      const updatedOrder = await onUpdateStatus(payload);

      setOrderState(updatedOrder);
      setSavedStatus(draftStatus);
      setSavedEta(isEtaRelevantStatus(draftStatus) ? etaDays : null);
      setSavedDeclineCode(
        draftStatus === "declined"
          ? (draftDeclineCode as DeclineReasonCode)
          : null,
      );
      setSavedDeclineNote(draftStatus === "declined" ? draftDeclineNote : "");
      setShowDeclineConfirm(false);
      const message = t(keys.orders.statusSavedMicro);
      setSaveSuccess(message);
      toast.success(message);
    } catch (caughtError) {
      if (caughtError instanceof ApiJsonClientError) {
        setSaveError(mapStatusUpdateError(caughtError, draftStatus, t));
      } else {
        setSaveError(t(keys.errors.generic));
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div
        id="status-history"
        className="rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-6 py-5 shadow-[var(--shadow-sm)]"
      >
        <div className="text-sm text-[color:var(--text-muted)]">
          {t(keys.orders.orderCodeLabel)}:{" "}
          <span className="font-semibold text-[color:var(--text-primary)]">
            {orderState.orderCode}
          </span>
        </div>
        <div className="mt-2 text-sm text-[color:var(--text-muted)]">
          {t(keys.orders.paymentMethodLabel)}:{" "}
          {getPaymentMethodLabel(orderState.paymentMethod as PaymentMethod, t)}
        </div>
        <div className="mt-2 text-sm text-[color:var(--text-muted)]">
          {t(keys.orders.totalLabel)}:{" "}
          {formatCurrency(orderState.totalAmount, locale)}
        </div>
        {orderState.couponCode ? (
          <div className="mt-2 text-sm text-[color:var(--text-muted)]">
            {t(keys.orders.couponLabel)}:{" "}
            <span className="font-mono font-semibold text-[color:var(--text-primary)]">
              {orderState.couponCode}
            </span>
            {orderState.couponDiscountAmount > 0 ? (
              <span className="ms-1 text-[color:var(--success)]">
                (−{formatCurrency(orderState.couponDiscountAmount, locale)})
              </span>
            ) : null}
          </div>
        ) : null}
        {canReviewPayment ? (
          <div className="mt-3">
            <Button
              type="button"
              variant="primary"
              className="h-9 rounded-lg px-3 text-xs font-semibold"
              onClick={() => setIsReviewDialogOpen(true)}
            >
              {t(keys.orders.reviewAction)}
            </Button>
          </div>
        ) : null}
      </div>

      <OnlinePaymentPanel
        storeId={storeId}
        orderId={orderState.id}
        locale={locale}
      />

      <div className="rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-6 py-5 shadow-[var(--shadow-sm)]">
        <div className="text-sm font-semibold text-[color:var(--text-primary)]">
          {t(keys.orders.statusManagementTitle)}
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-2 text-xs text-[color:var(--text-muted)]">
            {t(keys.orders.statusLabel)}
            <Combobox
              options={statusOptions}
              value={draftStatus}
              onChange={(value) =>
                setDraftStatus((value ?? draftStatus) as OrderStatus)
              }
            />
          </div>

          {isEtaRelevantStatus(draftStatus) ? (
            <label className="flex flex-col gap-2 text-xs text-[color:var(--text-muted)]">
              {t(keys.orders.estimatedDeliveryLabel)}
              <NumberInput
                name="estimatedDeliveryDays"
                min={0}
                value={draftEta}
                onChange={(event) => setDraftEta(event.target.value)}
              />
              <span className="text-[13px] text-[color:var(--text-muted)]">
                {t(keys.orders.estimatedDeliveryHelper)}
              </span>
            </label>
          ) : null}
        </div>

        {draftStatus === "declined" ? (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-2 text-xs text-[color:var(--text-muted)]">
              {t(keys.orders.declineReasonLabel)}
              <Combobox
                options={[
                  { value: "", label: t(keys.orders.declineReasonPlaceholder) },
                  ...declineOptions.map((option) => ({
                    value: option.value,
                    label: t(option.labelKey),
                  })),
                ]}
                value={draftDeclineCode}
                onChange={(value) =>
                  setDraftDeclineCode((value ?? "") as DeclineReasonCode | "")
                }
              />
            </div>
            <label className="flex flex-col gap-2 text-xs text-[color:var(--text-muted)]">
              {t(keys.orders.declineNoteLabel)}
              <input
                type="text"
                value={draftDeclineNote}
                onChange={(event) => setDraftDeclineNote(event.target.value)}
                className="h-11 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-sm"
              />
            </label>
          </div>
        ) : null}

        <div className="mt-4 flex items-center gap-2">
          <Button
            type="button"
            variant="primary"
            className="h-10 rounded-xl px-4 text-sm font-semibold"
            disabled={!hasChanges || isSaving}
            onClick={() => void submitStatusUpdate(false)}
          >
            {isSaving
              ? t(keys.orders.savingAction)
              : t(keys.orders.updateStatusAction)}
          </Button>
          {saveSuccess ? (
            <span className="text-xs text-[color:var(--success-500)]">
              {saveSuccess}
            </span>
          ) : null}
          {saveError ? (
            <span className="text-xs text-[color:var(--error-500)]">
              {saveError}
            </span>
          ) : null}
        </div>
      </div>

      <div className="rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-6 py-5 shadow-[var(--shadow-sm)]">
        <div className="text-sm font-semibold text-[color:var(--text-primary)]">
          {t(keys.orders.buyerInfoTitle)}
        </div>
        {/* TODO(privacy-mode): when privacy mode is enabled, mask phone/address values in details too. */}
        <div className="mt-3 text-sm text-[color:var(--text-muted)]">
          {t(keys.orders.buyerNameLabel)}:{" "}
          <span className="font-semibold text-[color:var(--text-primary)]">
            {orderState.customer.name}
          </span>
        </div>
        <div className="mt-2 text-sm text-[color:var(--text-muted)]">
          {t(keys.orders.buyerPhoneLabel)}: {orderState.customer.phoneE164}
        </div>
        <div className="mt-2 text-sm text-[color:var(--text-muted)]">
          {t(keys.orders.buyerAddressLabel)}:{" "}
          <OrderAddressBlock
            address={orderState.address}
            fallback={orderState.buyerAddress}
          />
        </div>
        <div className="mt-2 text-sm text-[color:var(--text-muted)]">
          {t(keys.orders.buyerNoteLabel)}:{" "}
          {orderState.buyerNote?.trim()
            ? orderState.buyerNote
            : t(keys.common.empty)}
        </div>
      </div>

      <div className="rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-6 py-5 shadow-[var(--shadow-sm)]">
        <div className="text-sm font-semibold text-[color:var(--text-primary)]">
          {t(keys.orders.itemsTitle)}
        </div>
        <div className="mt-4 flex flex-col gap-3 text-sm">
          {orderState.items.map((item, index) => (
            <div
              key={`${item.productVariantId}-${index}`}
              className="flex items-start justify-between gap-3"
            >
              <div className="min-w-0 text-[color:var(--text-secondary)]">
                <div className="truncate">
                  {locale === "en"
                    ? (item.resolvedTitleEn ??
                      item.productNameEn ??
                      item.resolvedTitleAr ??
                      item.productNameAr)
                    : (item.resolvedTitleAr ?? item.productNameAr)}
                </div>
                {item.selectedOptions.length > 0 ? (
                  <div className="mt-1 text-xs text-[color:var(--text-muted)]">
                    {item.selectedOptions
                      .map((option) =>
                        locale === "en"
                          ? (option.value.en ?? option.value.ar)
                          : option.value.ar,
                      )
                      .join(" • ")}
                  </div>
                ) : null}
              </div>
              <div className="shrink-0 text-[color:var(--text-muted)]">
                {item.quantity} × {formatCurrency(item.unitPrice, locale)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-6 py-5 shadow-[var(--shadow-sm)]">
        <div className="text-sm font-semibold text-[color:var(--text-primary)]">
          {t(keys.orders.statusHistoryTitle)}
        </div>
        {(orderState.statusHistory?.length ?? 0) === 0 ? (
          <p className="mt-2 text-xs text-[color:var(--text-muted)]">
            {t(keys.common.empty)}
          </p>
        ) : (
          <div className="mt-4 flex flex-col gap-3 text-sm">
            {orderState.statusHistory.map((entry) => (
              <div
                key={entry.id}
                className="rounded-xl border border-[color:var(--border-light)] px-3 py-2"
              >
                <div className="font-semibold text-[color:var(--text-primary)]">
                  {getOrderStatusLabel(t, entry.oldStatus)} {"->"}{" "}
                  {getOrderStatusLabel(t, entry.newStatus)}
                </div>
                <div className="mt-1 text-xs text-[color:var(--text-muted)]">
                  {new Date(entry.changedAt).toLocaleString(locale)}
                </div>
                {entry.statusEtaDays !== null ? (
                  <div className="mt-1 text-xs text-[color:var(--text-secondary)]">
                    {t(keys.orders.estimatedDeliveryLabel)}:{" "}
                    {entry.statusEtaDays}
                  </div>
                ) : null}
                {entry.note ? (
                  <div className="mt-1 text-xs text-[color:var(--text-secondary)]">
                    {entry.note}
                  </div>
                ) : null}
                {entry.declineReasonNote ? (
                  <div className="mt-1 text-xs text-[color:var(--text-secondary)]">
                    {entry.declineReasonNote}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-6 py-5 text-sm text-[color:var(--text-muted)] shadow-[var(--shadow-sm)]">
        <div className="text-sm font-semibold text-[color:var(--text-primary)]">
          {t(keys.orders.proofUploadedLabel)}
        </div>
        {isProofLoading ? (
          <div className="mt-3 text-xs text-[color:var(--text-muted)]">
            {t(keys.common.loading)}
          </div>
        ) : proofError ? (
          <div className="mt-3 text-xs text-[color:var(--error-500)]">
            {t(keys.orders.proofLoadError)}
          </div>
        ) : proofItems.length === 0 ? (
          <div className="mt-3 text-xs text-[color:var(--text-muted)]">
            {t(keys.common.empty)}
          </div>
        ) : (
          <div className="mt-4 flex flex-col gap-4">
            {proofItems.map((proof) => (
              <div
                key={proof.proofId}
                className="rounded-xl border border-[color:var(--border-light)] p-3"
              >
                <div className="relative h-80 w-full">
                  <img
                    src={proof.url}
                    alt={t(keys.orders.proofUploadedLabel)}
                    className="rounded-xl object-contain"
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
                <div className="mt-2 text-xs">
                  {formatCurrency(proof.paidAmount, locale)}
                </div>
                <div className="mt-1 text-xs">
                  {new Date(proof.uploadedAt).toLocaleString(locale)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={showDeclineConfirm}
        title={t(keys.orders.confirmDeclineTitle)}
        body={t(keys.orders.confirmDeclineBody)}
        confirmLabel={
          isSaving
            ? t(keys.orders.savingAction)
            : t(keys.orders.confirmDeclineAction)
        }
        cancelLabel={t(keys.common.cancel)}
        isSubmitting={isSaving}
        onCancel={() => setShowDeclineConfirm(false)}
        onConfirm={() => void submitStatusUpdate(true)}
      />

      <PaymentReviewDialog
        locale={locale}
        storeId={storeId}
        orderId={isReviewDialogOpen ? orderState.id : null}
        isOpen={isReviewDialogOpen}
        onClose={() => setIsReviewDialogOpen(false)}
        onSuccess={(updatedOrder) => {
          setOrderState(updatedOrder);
          setDraftStatus(updatedOrder.status);
          setDraftEta(normalizeEtaValue(updatedOrder.statusEtaDays));
          setSavedStatus(updatedOrder.status);
          setSavedEta(updatedOrder.statusEtaDays);
          setSavedDeclineCode(updatedOrder.declineReasonCode);
          setSavedDeclineNote(updatedOrder.declineReasonNote ?? "");
          setIsReviewDialogOpen(false);
        }}
      />
    </div>
  );
}
